import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "@repo/o37-ui";
import { ArrowLeft, Table, LayoutGrid, BarChart3, Settings, MessageSquare, Plus, FileText, Building2, Clock } from "lucide-react";
import KanbanBoard from '../../components/Fundraising/KanbanBoard';
import FundLeadTable from '../../components/Fundraising/FundLeadTable';
import TermSheetLeadTable from '../../components/Fundraising/TermSheetLeadTable';
import TermSheetKanbanBoard from '../../components/Fundraising/TermSheetKanbanBoard';
import FundModal from '../../components/Fundraising/FundModal';
import RaiseAnalytics from '../../components/Fundraising/RaiseAnalytics';
import RaiseActivities from '../../components/Fundraising/RaiseActivities';
import RaiseSettingsPanel from '../../components/Fundraising/RaiseSettingsPanel';
import { Fundraise, FundraisingFund, FundraisingAnalytics, TermSheetStatus, CapTableEntry } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

type TabType = 'table' | 'kanban' | 'activities' | 'analytics' | 'settings';

export default function RaiseDetail() {
  const { raiseId } = useParams<{ raiseId: string }>();
  const navigate = useNavigate();
  
  const [fundraise, setFundraise] = useState<Fundraise | null>(null);
  const [funds, setFunds] = useState<FundraisingFund[]>([]);
  const [analytics, setAnalytics] = useState<FundraisingAnalytics | null>(null);
  const [capTable, setCapTable] = useState<CapTableEntry[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('table');
  const [loading, setLoading] = useState(true);
  const [showFundModal, setShowFundModal] = useState(false);
  const [editingFund, setEditingFund] = useState<FundraisingFund | null>(null);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    if (raiseId && raiseId !== 'new') {
      loadFundraise();
      loadFunds();
      loadAnalytics();
      loadCapTable();
    } else {
      setLoading(false);
    }
  }, [raiseId]);

  const loadFundraise = async () => {
    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${raiseId}`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setFundraise({
          id: data.id,
          name: data.name,
          description: data.description,
          status: data.status,
          raiseGoal: data.raise_goal,
          linkedVdrId: data.linked_vdr_id,
          linkedVdr: data.vdr_rooms,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        });
      }
    } catch (e) {
      console.error('Failed to load fundraise:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadFunds = async () => {
    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${raiseId}/funds`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        const transformed = data.map((f: any) => ({
          id: f.id,
          fundraiseId: f.fundraise_id,
          name: f.name,
          website: f.website,
          logoUrl: f.logo_url,
          thesis: f.thesis,
          typicalTicketMin: f.typical_ticket_min,
          typicalTicketMax: f.typical_ticket_max,
          portfolioCompanies: f.portfolio_companies,
          stage: f.stage,
          priority: f.priority,
          notes: f.notes,
          createdAt: f.created_at,
          updatedAt: f.updated_at,
          contacts: f.fundraising_contacts?.map((c: any) => ({
            id: c.id,
            name: c.name,
            email: c.email,
            title: c.title,
            isPrimary: c.is_primary
          })),
          termSheets: f.fundraising_term_sheets?.map((ts: any) => ({
            id: ts.id,
            investmentAmount: ts.investment_amount,
            preMoneyValuation: ts.pre_money_valuation,
            status: ts.status
          }))
        }));
        setFunds(transformed);
      }
    } catch (e) {
      console.error('Failed to load funds:', e);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${raiseId}/analytics`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (e) {
      console.error('Failed to load analytics:', e);
    }
  };

  const loadCapTable = async () => {
    try {
      const response = await fetch(`${API_URL}/fundraising/raises/${raiseId}/cap-table`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        // Transform snake_case to camelCase
        const transformed = data.map((entry: any) => ({
          id: entry.id,
          fundraiseId: entry.fundraise_id,
          name: entry.name,
          type: entry.type,
          ownershipPercentage: parseFloat(entry.ownership_percentage),
          shares: entry.shares,
          notes: entry.notes,
          isActive: entry.is_active,
          createdAt: entry.created_at,
          updatedAt: entry.updated_at
        }));
        setCapTable(transformed);
      }
    } catch (e) {
      console.error('Failed to load cap table:', e);
    }
  };

  const handleStageChange = async (fundId: string, newStage: string) => {
    if (!raiseId) return;
    
    // Optimistic update
    setFunds(prev => prev.map(f => 
      f.id === fundId ? { ...f, stage: newStage as any } : f
    ));

    try {
      await fetch(`${API_URL}/fundraising/raises/${raiseId}/funds/${fundId}/stage`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({ stage: newStage })
      });
      // Reload analytics
      loadAnalytics();
    } catch (e) {
      // Revert on error
      loadFunds();
    }
  };

  const handleFundSaved = () => {
    setShowFundModal(false);
    setEditingFund(null);
    loadFunds();
    loadAnalytics();
  };

  const handleEditFund = (fund: FundraisingFund) => {
    setEditingFund(fund);
    setShowFundModal(true);
  };

  const handleTermSheetStatusChange = async (termSheetId: string, fundId: string, newStatus: TermSheetStatus) => {
    // Optimistic update
    setFunds(prev => prev.map(f => {
      if (f.id === fundId && f.termSheets) {
        return {
          ...f,
          termSheets: f.termSheets.map(ts =>
            ts.id === termSheetId ? { ...ts, status: newStatus } : ts
          )
        };
      }
      return f;
    }));

    try {
      await fetch(`${API_URL}/fundraising/funds/${fundId}/term-sheets/${termSheetId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({ status: newStatus })
      });
      // Reload analytics
      loadAnalytics();
    } catch (e) {
      // Revert on error
      loadFunds();
    }
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700';
      case 'paused': return 'bg-amber-100 text-amber-700';
      case 'closed': return 'bg-neutral-100 text-neutral-500';
      default: return 'bg-neutral-100 text-neutral-500';
    }
  };

  const handleOpenAIChat = () => {
    // This will be implemented later - placeholder for AI chat functionality
    console.log('Open AI Chat for raise:', raiseId);
    // For now, we'll dispatch a custom event that AdminPanel can listen to
    window.dispatchEvent(new CustomEvent('openAIChat', { detail: { raiseId } }));
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-6 border-b border-neutral-200">
          <h1 className="text-xl font-semibold">Loading...</h1>
        </div>
      </div>
    );
  }

  if (!fundraise) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-6 border-b border-neutral-200">
          <Button
            variant="ghost"
            onClick={() => navigate('/fundraising/raises')}
            icon={<ArrowLeft className="w-4 h-4" />}
            text="Back to Raises"
            className="mb-4"
          />
          <h1 className="text-xl font-semibold">Raise not found</h1>
        </div>
      </div>
    );
  }

  const tabs: { id: TabType; label: string; icon: React.ElementType }[] = [
    { id: 'table', label: 'Table', icon: Table },
    { id: 'kanban', label: 'Kanban', icon: LayoutGrid },
    { id: 'activities', label: 'Activities', icon: Clock },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Top Navigation Bar - Full Width */}
      <div className="flex-shrink-0 bg-white border-b border-neutral-200">
        <div className="px-6 py-4">
          {/* Back button and title */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/fundraising/raises')}
                icon={<ArrowLeft className="w-4 h-4" />}
                className="h-8 w-8 p-0"
              />
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-semibold">{fundraise.name}</h1>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(fundraise.status)}`}>
                    {fundraise.status.charAt(0).toUpperCase() + fundraise.status.slice(1)}
                  </span>
                </div>
                {fundraise.description && (
                  <p className="text-sm text-neutral-500 mt-0.5">{fundraise.description}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {activeTab !== 'settings' && activeTab !== 'analytics' && (
                <Button
                  onClick={() => {
                    setEditingFund(null);
                    setShowFundModal(true);
                  }}
                  icon={<Plus className="w-4 h-4" />}
                  text="Add Fund"
                />
              )}
              <Button
                variant="outline"
                onClick={handleOpenAIChat}
                icon={<MessageSquare className="w-4 h-4" />}
                text="AI Assistant"
              />
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center justify-between">
            <nav className="flex items-center gap-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-neutral-100 text-neutral-900'
                      : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>

            {/* Quick Stats in header */}
            {analytics && activeTab !== 'analytics' && activeTab !== 'settings' && (
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <span className="text-neutral-500">Funds:</span>
                  <span className="ml-1 font-medium">{analytics.totalFunds}</span>
                </div>
                <div>
                  <span className="text-neutral-500">Committed:</span>
                  <span className="ml-1 font-medium">{formatCurrency(analytics.committedCapital)}</span>
                </div>
                <div>
                  <span className="text-neutral-500">Progress:</span>
                  <span className={`ml-1 font-medium ${analytics.oversubscriptionPct >= 100 ? 'text-emerald-600' : ''}`}>
                    {analytics.oversubscriptionPct}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'table' && (
          <div className="space-y-8">
            {/* Funds Section */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-neutral-400" />
                <h2 className="text-lg font-semibold text-neutral-900">Funds</h2>
                <span className="text-sm text-neutral-500">({funds.length})</span>
              </div>
              <FundLeadTable
                funds={funds}
                onEditFund={handleEditFund}
                onStageChange={handleStageChange}
              />
            </section>

            {/* Term Sheets Section */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-neutral-400" />
                <h2 className="text-lg font-semibold text-neutral-900">Term Sheets</h2>
                <span className="text-sm text-neutral-500">
                  ({funds.reduce((sum, f) => sum + (f.termSheets?.length || 0), 0)})
                </span>
              </div>
              <TermSheetLeadTable
                funds={funds}
                onStatusChange={handleTermSheetStatusChange}
              />
            </section>
          </div>
        )}

        {activeTab === 'kanban' && (
          <div className="space-y-8">
            {/* Funds Kanban Section */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-neutral-400" />
                <h2 className="text-lg font-semibold text-neutral-900">Funds Pipeline</h2>
                <span className="text-sm text-neutral-500">({funds.length})</span>
              </div>
              <KanbanBoard
                funds={funds}
                onStageChange={handleStageChange}
                onEditFund={handleEditFund}
                onViewFund={(fund) => navigate(`/fundraising/funds/${fund.id}`)}
              />
            </section>

            {/* Term Sheets Kanban Section */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-neutral-400" />
                <h2 className="text-lg font-semibold text-neutral-900">Term Sheets Pipeline</h2>
                <span className="text-sm text-neutral-500">
                  ({funds.reduce((sum, f) => sum + (f.termSheets?.length || 0), 0)})
                </span>
              </div>
              <TermSheetKanbanBoard
                funds={funds}
                onStatusChange={handleTermSheetStatusChange}
              />
            </section>
          </div>
        )}

        {activeTab === 'activities' && (
            <RaiseActivities
                raiseId={raiseId!}
                funds={funds}
            />
        )}

        {activeTab === 'analytics' && analytics && (
          <RaiseAnalytics 
            raiseId={raiseId!} 
            analytics={analytics}
            funds={funds}
            capTable={capTable}
          />
        )}

        {activeTab === 'settings' && fundraise && (
          <RaiseSettingsPanel
            fundraise={fundraise}
            onUpdated={() => {
              loadFundraise();
              loadAnalytics();
            }}
          />
        )}
      </div>

      {/* Fund Modal */}
      {showFundModal && raiseId && (
        <FundModal
          fundraiseId={raiseId}
          fund={editingFund}
          onClose={() => {
            setShowFundModal(false);
            setEditingFund(null);
          }}
          onSaved={handleFundSaved}
        />
      )}
    </div>
  );
}
